import { createContext, useContext, useEffect } from 'react'
import type { ReactNode } from 'react'

import type { SettingsFormController, SettingsSectionId } from '../types/types'

export type RegisterSettingsDirtyState = (
  sectionId: SettingsSectionId,
  controller: SettingsFormController | undefined
) => void

/**
 * Section registrations are static objects built at module scope, so a
 * form-based section cannot hand the shell its `useSettingsFormState`
 * controller through the registration. This context is the runtime bridge:
 * `DarkSettingsPage` provides the registrar from `useSettingsPage`, and a
 * mounted section content component publishes its controller through
 * `useRegisterSettingsDirtyState`, which wires the shell's Save button and
 * the unsaved-changes guard.
 *
 * The default is a no-op (not a throw) so section content stays renderable
 * outside the shell — standalone tests and Storybook simply get no Save bar.
 */
const SettingsDirtyStateContext = createContext<RegisterSettingsDirtyState>(
  () => undefined
)

interface SettingsDirtyStateProviderIProps {
  children: ReactNode
  registerDirtyState: RegisterSettingsDirtyState
}

export const SettingsDirtyStateProvider = ({
  children,
  registerDirtyState,
}: SettingsDirtyStateProviderIProps) => (
  <SettingsDirtyStateContext.Provider value={registerDirtyState}>
    {children}
  </SettingsDirtyStateContext.Provider>
)

/**
 * Publishes a section's form controller to the Settings shell while the
 * caller is mounted. Pass `undefined` to withdraw it (e.g. while loading, or
 * when the current user cannot edit anything in the section) — the shell then
 * renders no Save button for the section.
 */
export const useRegisterSettingsDirtyState = (
  sectionId: SettingsSectionId,
  controller: SettingsFormController | undefined
) => {
  const registerDirtyState = useContext(SettingsDirtyStateContext)

  useEffect(() => {
    registerDirtyState(sectionId, controller)
    return () => registerDirtyState(sectionId, undefined)
  }, [controller, registerDirtyState, sectionId])
}
